import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const LABEL_WIDTH_DEFAULT = 168;
const LABEL_WIDTH_RSVP = 112;
const BAR_HEIGHT_DEFAULT = 34;
const BAR_HEIGHT_RSVP = 38;
const CONNECTOR_HEIGHT_DEFAULT = 14;

const getConnectorHeight = (topPct, bottomPct, variant) => {
  if (variant !== 'rsvp') {
    return CONNECTOR_HEIGHT_DEFAULT;
  }
  const delta = Math.abs(topPct - bottomPct);
  return Math.min(34, Math.max(12, 10 + delta * 0.24));
};

/**
 * Grey trapezoid between two centered bars (widths as % of plot area).
 */
const FunnelConnector = ({ topPct, bottomPct, variant, fill }) => {
  const height = getConnectorHeight(topPct, bottomPct, variant);
  const t = topPct / 2;
  const b = bottomPct / 2;
  const points = `${50 - t},0 ${50 + t},0 ${50 + b},${height} ${50 - b},${height}`;

  return (
    <Box sx={{ width: '100%', height, lineHeight: 0 }}>
      <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
        <polygon points={points} fill={fill} />
      </svg>
    </Box>
  );
};

const resolveBarWidthPct = (stage, variant) => {
  const pct = Math.min(100, Math.max(0, Number(stage.percentage) || 0));
  if (variant === 'rsvp') {
    if (stage.count > 0) {
      return Math.max(8, pct);
    }
    return Math.max(4, pct);
  }
  return Math.max(8, pct);
};

const FunnelBar = ({ stage, widthPct, variant }) => {
  const isRsvp = variant === 'rsvp';

  return (
    <Box
      sx={{
        width: `${widthPct}%`,
        minWidth: isRsvp ? undefined : widthPct < 12 ? 56 : 72,
        maxWidth: '100%',
        height: isRsvp ? BAR_HEIGHT_RSVP : BAR_HEIGHT_DEFAULT,
        bgcolor: stage.fill,
        borderRadius: isRsvp ? 0 : '3px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
      }}
    >
      <Typography
        sx={{
          color: '#FFFFFF',
          fontSize: isRsvp ? 13 : 12,
          fontWeight: 600,
          lineHeight: 1.2,
          textAlign: 'center',
          px: 0.5,
        }}
      >
        {isRsvp
          ? stage.count.toLocaleString()
          : `${stage.count.toLocaleString()} • ${stage.percentage}%`}
      </Typography>
    </Box>
  );
};

/**
 * Centered funnel with left labels + grey connectors.
 * @param {'default' | 'rsvp'} [variant] — `rsvp` matches partner Statistics funnel (count only, proportional bars).
 */
const ApexFunnelChart = ({ title, stages, variant = 'default' }) => {
  const theme = useTheme();
  if (!stages?.length) return null;

  const isRsvp = variant === 'rsvp';
  const labelWidth = isRsvp ? LABEL_WIDTH_RSVP : LABEL_WIDTH_DEFAULT;

  return (
    <Box
      sx={{
        p: isRsvp ? 2.5 : 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          letterSpacing: '-0.01em',
          mb: 2,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {stages.map((stage, index) => {
          const widthPct = resolveBarWidthPct(stage, variant);
          const prevWidthPct =
            index > 0 ? resolveBarWidthPct(stages[index - 1], variant) : widthPct;

          return (
            <Box key={stage.name}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `${labelWidth}px 1fr`,
                  alignItems: 'center',
                  columnGap: isRsvp ? 2 : 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: isRsvp ? 13 : 12,
                    fontWeight: 500,
                    color: 'text.primary',
                    textAlign: isRsvp ? 'left' : 'right',
                    lineHeight: 1.3,
                    pr: isRsvp ? 0 : 0.5,
                  }}
                >
                  {stage.name}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: isRsvp ? '100%' : 360,
                    mx: 'auto',
                    px: isRsvp ? 0.5 : 0,
                  }}
                >
                  {index > 0 && (
                    <FunnelConnector
                      topPct={prevWidthPct}
                      bottomPct={widthPct}
                      variant={variant}
                      fill={theme.palette.text.disabled}
                    />
                  )}
                  <FunnelBar stage={stage} widthPct={widthPct} variant={variant} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ApexFunnelChart;
